import * as React from "react";
import { Share, PlusSquare } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type Platform = "ios-safari" | "ios-chrome" | "android" | null;

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function getPlatform(): Platform {
  if (typeof window === "undefined") return null;
  const ua = window.navigator.userAgent;
  const isIOS =
    /iPhone|iPad|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  if (isIOS) {
    if (/CriOS|EdgiOS|FxiOS|OPiOS|GSA/.test(ua)) return "ios-chrome";
    return "ios-safari";
  }
  if (/Android/.test(ua)) return "android";
  return null;
}

function StepNumber({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
      {children}
    </span>
  );
}

function AndroidInstructions() {
  return (
    <ol className="space-y-4 text-sm">
      <li className="flex gap-3">
        <StepNumber>1</StepNumber>
        <span className="text-muted-foreground">
          Kliknij <strong>trzy kropki</strong> (⋯) w prawym górnym rogu Chrome.
        </span>
      </li>
      <li className="flex gap-3">
        <StepNumber>2</StepNumber>
        <span className="text-muted-foreground">
          Z rozwiniętej listy wybierz{" "}
          <strong>„Zainstaluj i utwórz skrót"</strong>.
        </span>
      </li>
      <li className="flex gap-3">
        <StepNumber>3</StepNumber>
        <span className="text-muted-foreground">
          W oknie instalacji kliknij <strong>„Zainstaluj"</strong>. Gotowe!
        </span>
      </li>
    </ol>
  );
}

function IOSInstructions({ browserLabel }: { browserLabel: string }) {
  return (
    <ol className="space-y-4 text-sm">
      <li className="flex gap-3">
        <StepNumber>1</StepNumber>
        <span className="text-muted-foreground">
          Kliknij przycisk <strong>⋯</strong> (trzy kropki) w prawym dolnym rogu{" "}
          {browserLabel}.
        </span>
      </li>
      <li className="flex gap-3">
        <StepNumber>2</StepNumber>
        <span className="text-muted-foreground">
          Z menu wybierz{" "}
          <Share className={cn("inline h-4 w-4 align-text-bottom text-primary")} />{" "}
          <strong>Udostępnij</strong>.
        </span>
      </li>
      <li className="flex gap-3">
        <StepNumber>3</StepNumber>
        <span className="text-muted-foreground">
          W arkuszu udostępniania kliknij{" "}
          <strong>„Do ekranu głównego"</strong>.
        </span>
      </li>
      <li className="flex gap-3">
        <StepNumber>4</StepNumber>
        <span className="text-muted-foreground">
          Kliknij <strong>„Dodaj"</strong> w prawym górnym rogu. Gotowe!
        </span>
      </li>
    </ol>
  );
}

interface InstallInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform?: Platform;
}

export default function InstallInstructionsDialog({
  open,
  onOpenChange,
}: InstallInstructionsDialogProps) {

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-sm overflow-y-auto md:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-center">
            Jak dodać na ekran główny
          </DialogTitle>
          <DialogDescription className="text-center">
            Wykonaj te kroki w przeglądarce na telefonie.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-6">
          <section>
            <h4 className="mb-3 text-sm font-semibold text-foreground">
              Chrome na Androidzie
            </h4>
            <AndroidInstructions />
          </section>
          <div className="border-t" />
          <section>
            <h4 className="mb-3 text-sm font-semibold text-foreground">
              Safari na iPhone
            </h4>
            <IOSInstructions browserLabel="Safari" />
          </section>
        </div>


        <div className="mt-4 flex items-center gap-3 rounded-lg border bg-muted/50 p-3">
          <PlusSquare className="h-5 w-5 shrink-0 text-primary" />
          <p className="text-xs text-muted-foreground">
            Ikona LPU pojawi się na ekranie głównym i otworzy się jak zwykła
            aplikacja.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
